import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AdminBanner,
  type BannerCreateInput,
  type BannerUpdateInput,
  adminCreateBanner,
  adminDeleteBanner,
  adminListBanners,
  adminUpdateBanner,
} from '../api/banners'
import { ACTIVE_BANNERS_KEY } from './useBanners'

const ADMIN_BANNERS_KEY: readonly ['admin', 'banners'] = ['admin', 'banners']

export function useAdminBanners() {
  return useQuery<AdminBanner[]>({
    queryKey: ADMIN_BANNERS_KEY,
    queryFn: adminListBanners,
    staleTime: 30 * 1000,
  })
}

function useInvalidateBanners() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_BANNERS_KEY })
    void qc.invalidateQueries({ queryKey: ACTIVE_BANNERS_KEY })
  }
}

export function useCreateBanner() {
  const invalidate = useInvalidateBanners()
  return useMutation({
    mutationFn: (input: BannerCreateInput) => adminCreateBanner(input),
    onSuccess: invalidate,
  })
}

export function useUpdateBanner() {
  const invalidate = useInvalidateBanners()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: BannerUpdateInput }) =>
      adminUpdateBanner(id, input),
    onSuccess: invalidate,
  })
}

export function useDeleteBanner() {
  const invalidate = useInvalidateBanners()
  return useMutation({
    mutationFn: (id: string) => adminDeleteBanner(id),
    onSuccess: invalidate,
  })
}
